import React, { useState, useEffect } from 'react';
import CommonTable from '../../components/commonComponent/CommanTable';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';

const tabConfig = [
  {
    key: 'unit',
    label: 'Unit',
    addLabel: 'Add New Unit',
    columns: [
      { key: 'srNo', label: 'Sr.no:' },
      { key: 'name', label: 'Unit Name' },
      { key: 'actions', label: 'Action' },
    ],
    modalFields: [
      { name: 'name', label: 'Unit Name', type: 'text', placeholder: 'Enter Unit Name' },
    ],
    initialValues: { name: '' },
    requiredFields: ['name'],
  },
];

const apiMap = {
  unit: '/admin/api/item-unit',
};

function ItemManage() {
  const [activeTab, setActiveTab] = useState('unit');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ name: '' });
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDefaultLoading, setIsDefaultLoading] = useState(false);
  const defaultLimit = process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 10;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const businessId = localStorage.getItem('businessId') || '';
  const currentTab = tabConfig.find((tab) => tab.key === activeTab);
  const [totalRecords, setTotalRecords] = useState(0);
  
  useEffect(() => {
    fetchTabData(activeTab, page, limit);
    // eslint-disable-next-line
  }, [activeTab, page, limit]);

  async function fetchTabData(tabKey, pageNo = 1, pageLimit = 10) {
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiMap[tabKey]}?business=${businessId}&page=${pageNo}&limit=${pageLimit}`
      );

      const items = res.data.units || [];
      setTotalRecords(res.data.total || 0);

      setData((prev) => ({
        ...prev,
        [tabKey]: items.map((item, idx) => ({
          srNo: (pageNo - 1) * pageLimit + idx + 1,
          ...item,
        })),
      }));
    } catch (err) {
      setData((prev) => ({ ...prev, [tabKey]: [] }));
    } finally {
      setLoading(false);
    }
  }

  async function handleDefault() {
    setIsDefaultLoading(true);
    try {
      await axios.post('/admin/api/default', {
        business: businessId,
        type: 'unit',
      });
      fetchTabData(activeTab, page, limit);
    } catch (err) {}
    finally {
      setIsDefaultLoading(false);
    }
  }

  function handleAdd() {
    setModalData(currentTab.initialValues);
    setErrors({});
    setShowModal(true);
  }

  function handleEdit(idx) {
    const editRow = data[activeTab]?.[idx];
    if (!editRow) return;
    const { actions, srNo, ...rawData } = editRow;
    setModalData(rawData);
    setErrors({});
    setShowModal(true);
  }

  async function handleDelete(idx) {
    const item = data[activeTab]?.[idx];
    if (!item?._id) return;

    const res = await confirmDelete(`Are you sure you want to delete ${item.name}?`);
    if (!res.isConfirmed) return;

    try {
      await axios.delete(`${apiMap[activeTab]}/${item._id}?business=${businessId}`);
      toastSuccess('Deleted successfully');
      fetchTabData(activeTab, page, limit);
    } catch (err) {
      toastError('Delete failed');
    }
  }

  async function handleSubmit() {
    const newErrors = {};
    currentTab.requiredFields.forEach((f) => {
      if (!modalData[f]) newErrors[f] = 'Required';
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    const payload = { ...modalData, business: businessId };

    try {
      if (modalData._id) {
        await axios.put(`${apiMap[activeTab]}/${modalData._id}`, payload);
      } else {
        await axios.post(apiMap[activeTab], payload);
      }
      fetchTabData(activeTab, page, limit);
      setShowModal(false);
      setModalData(currentTab.initialValues);
    } catch (err) {
      toastError('Save failed');
    }
  }

  function handleCancel() {
    setShowModal(false);
    setModalData(currentTab.initialValues);
    setErrors({});
  }

  const rows = (data[activeTab] || [])
    .filter((row) =>
      row.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .map((row, idx) => ({
      ...row,
      actions: (
        <div>
          <button
            className="btn btn-sm border-0 text-primary"
            onClick={() => handleEdit(idx)}
          >
            <FaPencilAlt size={14} />
          </button>
          <button
            className="btn btn-sm border-0 text-danger"
            onClick={() => handleDelete(idx)}
          >
            <FaTrashAlt size={14} />
          </button>
        </div>
      ),
    }));

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Item Setting</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-primary" onClick={handleAdd}>
            + Add New Unit
          </button>

          {data[activeTab]?.length === 0 && !loading && (
            <button
              className="btn btn-outline-primary"
              onClick={handleDefault}
              disabled={isDefaultLoading}
            >
              {isDefaultLoading ? 'Adding...' : 'Add Default'}
            </button>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-end mb-3">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: 250 }}
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <CommonTable
        columns={currentTab.columns}
        data={rows}
        page={page}
        totalItems={totalRecords}
        itemsPerPage={limit}
        onPageChange={setPage}
        onItemsPerPageChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
        itemsPerPageOptions={[10, 15, 25, 50]}
      />
      )}

      <CommonModalForm
        visible={showModal}
        title={modalData._id ? 'Edit Unit' : 'Add New Unit'}
        fields={currentTab.modalFields}
        values={modalData}
        onChange={setModalData}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        errors={errors}
        requiredFields={currentTab.requiredFields}
        submitLabel={modalData._id ? 'Update' : 'Save'}
      />
    </div>
  );
}

export default ItemManage;
